import { buildDemandInspirationTags } from '@sizzlenz/shared';

import type { DemandExample, PostAnalysis, PostMeta } from '@/components/demand/ExampleCard';

export interface FeedItem {
  example: DemandExample;
  tags: string[];
  variant?: 'compact' | 'intelligence';
  postMeta?: PostMeta;
  analysis?: PostAnalysis;
}

type FeedItemInput = Omit<FeedItem, 'tags'>;

const createFeedItem = (item: FeedItemInput): FeedItem => ({
  ...item,
  tags: buildDemandInspirationTags(item.example.attributes),
});

/**
 * Static feed examples — shaped like future content records, including generated
 * namespaced tags for API filtering when this moves to useContent.
 */
export const FEED_EXAMPLES: FeedItem[] = [
  createFeedItem({
    variant: 'intelligence',
    example: {
      id: 'eden-orchards-woolworths-sampling',
      brand: 'Eden Orchards × Woolworths',
      whatTheyDid:
        'Ran a nationwide Pure Cherry Juice sampling campaign across 15 Woolworths stores, pairing product trial with one-to-one education at the shelf.',
      whyItWorks:
        'It puts trial beside the buying decision. For a functional drink, a quick taste plus a clear benefits story can shorten the path from curiosity to purchase.',
      source: 'LinkedIn',
      attributes: {
        demandRoles: ['brand-building', 'sales-activation'],
        journeyStages: ['trial', 'intent', 'purchase'],
        primaryTransition: { from: 'trial', to: 'purchase' },
        demandObjectives: ['prompt-action', 'make-purchase-easy'],
        tactics: ['product-sampling'],
        channels: ['retail-in-store'],
        deliveryDisciplines: ['activation-staffing', 'measurement-optimisation'],
        deliveryActivities: [
          'sampling-coordination',
          'activation-staffing',
          'shopper-education',
          'campaign-measurement-reporting',
        ],
      },
    },
    postMeta: {
      handle: '@destroactivations',
      timeAgo: 'May–Jun 2026',
      caption:
        'Eden Orchards Pure Cherry Juice sampling, bringing sleep, recovery and wellbeing education directly to supermarket shoppers.',
      likes: '—',
      comments: '—',
      shares: '—',
    },
    analysis: {
      performanceLabel: 'Campaign execution snapshot',
      metrics: [
        { value: '15', label: 'Stores', colorClass: 'border-green-500 text-green-600' },
        { value: '11', label: 'Ambassadors', colorClass: 'border-blue-500 text-blue-600' },
        {
          value: '4h',
          label: 'Active sampling / shift',
          colorClass: 'border-orange-500 text-orange-600',
        },
      ],
      whyItWorked: [
        'Trial happens at the point of purchase',
        'Ambassadors explain functional benefits in person',
        'Peak shopping periods maximise shopper contact',
      ],
      audienceSignal: 'Supermarket shoppers seeking sleep, recovery + wellbeing products',
    },
  }),
  createFeedItem({
    variant: 'intelligence',
    example: {
      id: 'arepa-consumer-event-sampling',
      brand: 'Arepa',
      whatTheyDid:
        'Activated at the Food Show and New Zealand House & Garden Show in Auckland and Christchurch, using sampling and direct sales to introduce its neuroscience-backed beverage.',
      whyItWorks:
        'A premium functional drink needs a human explanation as well as a taste. Skilled ambassadors made complex benefits relatable, built trust with sceptical shoppers and encouraged trial.',
      source: 'Case study',
      attributes: {
        demandRoles: ['brand-building', 'sales-activation'],
        journeyStages: ['awareness', 'trial', 'purchase'],
        primaryTransition: { from: 'awareness', to: 'trial' },
        demandObjectives: ['create-desire', 'build-preference', 'prompt-action'],
        tactics: ['product-sampling', 'product-demonstration', 'experiential-activation'],
        channels: ['consumer-events'],
        deliveryDisciplines: ['activation-staffing', 'logistics-distribution'],
        deliveryActivities: [
          'sampling-coordination',
          'activation-staffing',
          'shopper-education',
          'event-setup',
        ],
      },
    },
    postMeta: {
      handle: 'Social Smiths',
      timeAgo: '27–29 Mar 2026',
      caption:
        'Arepa sampling activation: translating mental clarity and performance benefits into clear conversations at two major consumer events.',
      likes: '—',
      comments: '—',
      shares: '—',
    },
    analysis: {
      performanceLabel: 'Campaign execution snapshot',
      metrics: [
        { value: '4', label: 'Ambassadors', colorClass: 'border-green-500 text-green-600' },
        { value: '2', label: 'Cities', colorClass: 'border-blue-500 text-blue-600' },
        { value: '2', label: 'Consumer events', colorClass: 'border-purple-500 text-purple-600' },
      ],
      whyItWorked: [
        'Product education makes a complex proposition relatable',
        'Sampling lowers the barrier to trying a premium product',
        'Direct sales turn event interest into immediate action',
      ],
      audienceSignal:
        'Event attendees interested in mental clarity, performance + functional drinks',
    },
  }),
  createFeedItem({
    variant: 'intelligence',
    example: {
      id: 'harvest-table-flavour-launch',
      brand: 'Harvest Table',
      whatTheyDid:
        'New season flavour launch announced via Reels with bold text overlays and product close-ups.',
      whyItWorks:
        'Hook lands in the first line. Strong product close-ups. CTA earns saves and shares.',
      source: 'Instagram',
      attributes: {
        demandRoles: ['brand-building'],
        journeyStages: ['awareness', 'consideration'],
        primaryTransition: { from: 'awareness', to: 'consideration' },
        demandObjectives: ['get-noticed', 'create-desire'],
        tactics: ['product-launch', 'content-social-campaign'],
        channels: ['social'],
        deliveryDisciplines: ['creative-design', 'production'],
        deliveryActivities: ['campaign-creative-development', 'content-production'],
      },
    },
    postMeta: {
      handle: '@harvesttable',
      timeAgo: '2h',
      caption: 'Fresh, fast, full of crunch. Save this for your next lunch run.',
      likes: '1.8k',
      comments: '142',
      shares: '86',
    },
    analysis: {
      performanceLabel: 'High-performing post',
      metrics: [
        { value: '+205%', label: 'Reach', colorClass: 'border-green-500 text-green-600' },
        { value: '8.4%', label: 'Engage', colorClass: 'border-blue-500 text-blue-600' },
        { value: '27%', label: 'Saves' },
      ],
      whyItWorked: [
        'Hook lands in the first line',
        'Strong product close-ups',
        'CTA earns saves + shares',
      ],
      audienceSignal: 'Lunch ideas + behind-the-scenes content',
    },
  }),
  createFeedItem({
    variant: 'compact',
    example: {
      id: 'fix-and-fogg-in-store-sampling',
      brand: 'Fix & Fogg',
      whatTheyDid:
        "In-store sampling activation at New World — letting shoppers taste peanut butter variants they'd never pick up otherwise.",
      whyItWorks:
        'Removes the trial barrier entirely. People who taste buy. One interaction moves someone from unaware to purchased.',
      source: 'LinkedIn',
      attributes: {
        demandRoles: ['brand-building', 'sales-activation'],
        journeyStages: ['awareness', 'trial', 'purchase'],
        primaryTransition: { from: 'awareness', to: 'trial' },
        demandObjectives: ['prompt-action'],
        tactics: ['product-sampling'],
        channels: ['retail-in-store'],
        deliveryDisciplines: ['activation-staffing'],
        deliveryActivities: ['sampling-coordination', 'activation-staffing'],
      },
    },
  }),
  createFeedItem({
    variant: 'intelligence',
    example: {
      id: 'karma-drinks-hospitality-partnership',
      brand: 'Karma Drinks',
      whatTheyDid:
        'Partnered with hospitality venues to co-brand a summer activation — product featured as the house mixer.',
      whyItWorks:
        'Creates preference through context. People associate the drink with a great night out and seek it at retail.',
      source: 'Instagram',
      attributes: {
        demandRoles: ['brand-building', 'sales-activation'],
        journeyStages: ['consideration', 'intent', 'purchase'],
        primaryTransition: { from: 'consideration', to: 'intent' },
        demandObjectives: ['create-desire', 'build-preference', 'make-purchase-easy'],
        tactics: ['collaboration', 'experiential-activation'],
        channels: ['hospitality'],
        deliveryDisciplines: ['creative-design', 'activation-staffing'],
        deliveryActivities: ['campaign-creative-development', 'campaign-deployment'],
      },
    },
    postMeta: {
      handle: '@karmadrinks',
      timeAgo: '5h',
      caption: 'Summer sessions sorted. Ask for Karma Cola at your local.',
      likes: '920',
      comments: '67',
      shares: '43',
    },
    analysis: {
      performanceLabel: 'Strong engagement spike',
      metrics: [
        { value: '+140%', label: 'Reach', colorClass: 'border-green-500 text-green-600' },
        { value: '6.2%', label: 'Engage', colorClass: 'border-blue-500 text-blue-600' },
        { value: '19%', label: 'Shares' },
      ],
      whyItWorked: [
        'Venue context creates aspirational association',
        'Clear call-to-action drives retail search',
        'Summer timing matches buying intent',
      ],
      audienceSignal: 'Hospitality moments + conscious choices',
    },
  }),
  createFeedItem({
    variant: 'compact',
    example: {
      id: 'proper-crisps-limited-edition',
      brand: 'Proper Crisps',
      whatTheyDid:
        'Limited-edition seasonal flavour launched exclusively through indie retailers, promoted via email + social countdown.',
      whyItWorks:
        "Scarcity drives urgency. Existing fans buy immediately and share. Retailers get foot traffic from a product people can't get elsewhere.",
      source: 'Instagram',
      attributes: {
        demandRoles: ['brand-building', 'sales-activation'],
        journeyStages: ['awareness', 'intent', 'purchase', 'loyalty'],
        primaryTransition: { from: 'intent', to: 'purchase' },
        demandObjectives: ['get-noticed', 'prompt-action'],
        tactics: ['limited-edition-scarcity', 'product-launch'],
        channels: ['retail-in-store', 'email-crm', 'social'],
        deliveryDisciplines: ['creative-design', 'logistics-distribution'],
        deliveryActivities: ['campaign-creative-development', 'campaign-deployment'],
      },
    },
  }),
  createFeedItem({
    variant: 'compact',
    example: {
      id: 'pics-factory-tour-earned-media',
      brand: "Pic's Peanut Butter",
      whatTheyDid:
        'Factory tour experience turned into a social content engine — visitors film, tag, and share without prompting.',
      whyItWorks:
        'Earned media at scale. Every visitor becomes an unpaid ambassador. The brand stays in feeds without ad spend.',
      source: 'LinkedIn',
      attributes: {
        demandRoles: ['brand-building', 'retention-advocacy'],
        journeyStages: ['awareness', 'consideration', 'advocacy'],
        primaryTransition: { from: 'advocacy', to: 'awareness' },
        demandObjectives: ['get-noticed', 'create-desire'],
        tactics: ['experiential-activation', 'earned-media-advocacy'],
        channels: ['brand-owned-experience', 'social', 'earned-media'],
        deliveryDisciplines: ['creative-design', 'measurement-optimisation'],
        deliveryActivities: ['visitor-experience-design', 'content-production'],
      },
    },
  }),
  createFeedItem({
    variant: 'intelligence',
    example: {
      id: 'allpress-subscription-referral',
      brand: 'AllPress Espresso',
      whatTheyDid:
        'Subscription referral program — existing customers gift a free bag to a friend, both get a discount.',
      whyItWorks:
        'Turns repeat buyers into acquisition channels. The recommendation comes from a trusted person, not an ad.',
      source: 'Email',
      attributes: {
        demandRoles: ['retention-advocacy', 'brand-building'],
        journeyStages: ['loyalty', 'advocacy', 'awareness'],
        primaryTransition: { from: 'advocacy', to: 'awareness' },
        demandObjectives: ['encourage-repeat', 'get-noticed'],
        tactics: ['referral-programme', 'promotion-offer'],
        channels: ['email-crm', 'ecommerce-dtc'],
        deliveryDisciplines: ['strategy-insight', 'creative-design', 'measurement-optimisation'],
        deliveryActivities: [
          'offer-development',
          'campaign-creative-development',
          'referral-tracking',
        ],
      },
    },
    postMeta: {
      handle: '@allpressespresso',
      timeAgo: '1d',
      caption: 'Give coffee, get coffee. Tag someone who needs this.',
      likes: '2.3k',
      comments: '198',
      shares: '312',
    },
    analysis: {
      performanceLabel: 'Viral referral loop',
      metrics: [
        { value: '+380%', label: 'Reach', colorClass: 'border-green-500 text-green-600' },
        { value: '12.1%', label: 'Engage', colorClass: 'border-purple-500 text-purple-600' },
        { value: '41%', label: 'Shares', colorClass: 'border-orange-500 text-orange-600' },
      ],
      whyItWorked: [
        'Referral incentive drives tagging behaviour',
        'Simple mechanic — easy to understand',
        'Aligns with gifting culture',
      ],
      audienceSignal: 'Coffee rituals + gift-giving moments',
    },
  }),
];
